import { useState } from 'react'
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { FileWarning, MailCheck } from 'lucide-react'
import { Banner, Button } from '@shared/design-system/ui'
import { button as buttonRecipe } from '@shared/design-system/ui/Button/Button.css'
import { fadeIn } from '@shared/design-system/styles/motion.css'
import { useSession } from '@shared/lib/auth/session-store'
import { needsSaleRegistration } from '@shared/lib/auth/operation-eligibility'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { useResendVerificationEmailMutation } from '@features/auth/api/auth.mutations'
import { BottomNav } from './BottomNav'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { content, emailBanner, main, shell, shellBody } from './AppShell.css'
import { LegacyImportModal } from '@features/legacy-import/LegacyImportModal'

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'vendepay-sidebar-collapsed'

function initialSidebarCollapsed() {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed)
  const user = useSession((state) => state.user)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const showSaleRegistrationBanner =
    Boolean(user) && needsSaleRegistration(user?.documentationStatus) && pathname !== '/onboarding'
  const resendEmailMutation = useResendVerificationEmailMutation()

  function resendEmail() {
    resendEmailMutation.mutate(undefined, {
      onSuccess: () => toast.success('E-mail de confirmação reenviado.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível reenviar o e-mail',
            fallback: 'Tente novamente em alguns instantes.',
          }),
        ),
    })
  }

  function handleSidebarCollapsedChange(next: boolean) {
    setSidebarCollapsed(next)
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next))
    } catch {
      return
    }
  }

  return (
    <>
      <LegacyImportModal />
      <div className={shell} data-testid="app-shell">
        <Topbar />
        <div className={shellBody}>
          <Sidebar collapsed={sidebarCollapsed} onCollapsedChange={handleSidebarCollapsedChange} />
          <main className={main}>
            <div className={`${content} ${fadeIn}`}>
              {showSaleRegistrationBanner ? (
                <div className={emailBanner}>
                  <Banner
                    tone="danger"
                    title="Conclua seu cadastro para vender e receber."
                    icon={<FileWarning size={18} />}
                    action={
                      <Link
                        to="/onboarding"
                        className={buttonRecipe({ variant: 'secondary', size: 'sm' })}
                      >
                        Concluir cadastro
                      </Link>
                    }
                  >
                    Complete os dados do cadastro para liberar suas vendas e recebimentos.
                  </Banner>
                </div>
              ) : null}
              {user && !user.emailVerified ? (
                <div className={emailBanner}>
                  <Banner
                    tone="info"
                    title="Confirme seu e-mail"
                    icon={<MailCheck size={18} />}
                    action={
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={resendEmailMutation.isPending}
                        onClick={resendEmail}
                      >
                        {resendEmailMutation.isPending ? 'Enviando...' : 'Reenviar'}
                      </Button>
                    }
                  >
                    Enviamos o link para {user.email}.
                  </Banner>
                </div>
              ) : null}
              <Outlet />
            </div>
            <BottomNav />
          </main>
        </div>
      </div>
    </>
  )
}
